import { useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  Stack,
  IconButton,
  Container,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';

import FolderIcon from '@mui/icons-material/Folder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLocation } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import PageTitle from '../../components/PageHeading';
import Page from '../../components/Page';
import Label from '../../components/Label';
import BrudCrumbs from '../../components/BreadCrumbs';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: '#fff',
  borderBottom: '1px solid #f4f6f8',
  '&:last-child td, &:last-child th': { border: 0 }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    fontWeight: '700',
    padding: '16px 12px',
    color: '#637381',
    textTransform: 'uppercase',
    backgroundColor: '#f8fafc'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '14px 12px',
    borderBottom: '1px solid #f4f6f8'
  }
}));

const getLabelColor = (s) =>
  ({
    New: 'info',
    Contacted: 'info',
    Error: 'error',
    Poor: 'error',
    Rejected: 'error',
    Warning: 'warning',
    Fair: 'warning',
    'Review Required': 'warning',
    DND: 'warning',
    Pending: 'warning',
    'Not Fetched': 'default',
    Valid: 'success',
    Good: 'success',
    Excellent: 'success',
    Approved: 'success',
    Fetched: 'success',
    Submitted: 'success'
  }[s] || 'default');

const getCardTheme = (label) => {
  switch (label) {
    case 'No. of Records':
      return { iconBg: '#e0f2fe', iconColor: '#0284c7', borderColor: '#e2e8f0' };
    case 'No. of Records Bureau Fetched':
      return { iconBg: '#dcfce7', iconColor: '#16a34a', borderColor: '#bbf7d0' };
    case 'Bureau Not Fetched':
      return { iconBg: '#fee2e2', iconColor: '#dc2626', borderColor: '#fecaca' };
    case 'Phone - FPD':
      return { iconBg: '#fef3c7', iconColor: '#d97706', borderColor: '#fde68a' };
    default:
      return { iconBg: '#f1f5f9', iconColor: '#64748b', borderColor: '#e2e8f0' };
  }
};

export default function CheckBureau() {
  const location = useLocation();
  const { rowId } = location.state || {};

  const summaryCards = useMemo(
    () => [
      {
        icon: <FolderIcon fontSize="medium" />,
        value: 8,
        label: 'No. of Records'
      },
      {
        icon: <TrendingUpIcon fontSize="medium" />,
        value: 5,
        label: 'No. of Records Bureau Fetched'
      },
      {
        icon: <WarningAmberIcon fontSize="medium" />,
        value: 3,
        label: 'Bureau Not Fetched'
      },
      {
        icon: <AccessTimeIcon fontSize="medium" />,
        value: 0,
        label: 'Phone - FPD'
      }
    ],
    []
  );

  const customers = useMemo(
    () => [
      {
        name: 'Abhinav Sharma',
        mobile: '9805079253',
        cibilScore: 780,
        status: 'Good',
        recommendation: 'Approved',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Anil Kumar Parnami',
        mobile: '9194976544',
        cibilScore: 650,
        status: 'Fair',
        recommendation: 'Review Required',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Jagdish Joshi',
        mobile: '9189688332',
        cibilScore: 820,
        status: 'Excellent',
        recommendation: 'Approved',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Piyush Garodhara',
        mobile: '9876543210',
        cibilScore: 590,
        status: 'Poor',
        recommendation: 'Rejected',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Rahul Verma',
        mobile: '9988776655',
        cibilScore: null,
        status: 'Not Fetched',
        recommendation: 'Pending',
        bureauStatus: 'Not Fetched'
      },
      {
        name: 'Priya Singh',
        mobile: '9123456789',
        cibilScore: null,
        status: 'Not Fetched',
        recommendation: 'Pending',
        bureauStatus: 'Not Fetched'
      },
      {
        name: 'Amit Kumar',
        mobile: '9876501234',
        cibilScore: 720,
        status: 'Good',
        recommendation: 'Approved',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Neha Gupta',
        mobile: '9123987650',
        cibilScore: null,
        status: 'Not Fetched',
        recommendation: 'Pending',
        bureauStatus: 'Not Fetched'
      }
    ],
    []
  );

  return (
    <Page title="Partner Management System || Check Bureau">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            className="pageHeading"
          >
            <PageTitle info="Check Bureau" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          {/* -------- SUMMARY METRIC CARDS -------- */}
          <Grid container spacing={3} mb={3}>
            {summaryCards?.map((card, i) => {
              const theme = getCardTheme(card.label);
              return (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      borderRadius: '16px',
                      border: `1px solid ${theme.borderColor}`,
                      bgcolor: '#fff',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.02)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.06)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.iconBg,
                        color: theme.iconColor
                      }}
                    >
                      {card?.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>
                        {card?.value}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 500 }}>
                        {card?.label}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* -------- TABLE CARD -------- */}
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '20px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
              mb: 3
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #13519d 0%, #1565c0 100%)',
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}>
                Bureau Check Results
              </Typography>

              <Tooltip title="Download" placement="top" arrow>
                <IconButton
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                  }}
                  size="small"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>Mobile</StyledTableCell>
                    <StyledTableCell>CIBIL Score</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Recommendation</StyledTableCell>
                    <StyledTableCell>Bureau Status</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {customers?.map((r, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell sx={{ fontWeight: 600, color: '#13519d' }}>
                        {r.name}
                      </StyledTableCell>
                      <StyledTableCell sx={{ color: '#637381' }}>{r.mobile}</StyledTableCell>
                      <StyledTableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {r.cibilScore !== null ? r.cibilScore : '—'}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Label variant="ghost" color={getLabelColor(r.status)}>
                          {r.status}
                        </Label>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Label variant="ghost" color={getLabelColor(r.recommendation)}>
                          {r.recommendation}
                        </Label>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Label variant="ghost" color={getLabelColor(r.bureauStatus)}>
                          {r.bureauStatus}
                        </Label>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
